import { Button } from "@2u-landing-pages/components/Button";
import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { Tile } from "@2u-landing-pages/components/Tile";
import { useParseMeta, useProgramMetaFields } from "@2u-landing-pages/providers";
import { formatCurrency } from "@2u-landing-pages/utils";
import { renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { Body, ButtonGroup, Container, Heading } from "@red-digital/bricks";
import cn from "classnames";
import React, { useMemo } from "react";
import { CustomCSSProperties, DetailCalloutProps } from "../../DetailCallout.types";
// @ts-ignore
import styles from "./Default.module.scss";

export const Default: React.FC<DetailCalloutProps> = ({
  background_color,
  main_content,
  items,
  buttons,
  footnote,
  footnote_background_color,
  footnote_text_color,
  className,
  phoneNumber,
  tuition_color,
  display_tuition_cost,
  ctaLocation = "Detail Callout",
}) => {
  const style: CustomCSSProperties = {};
  const parseMetadata = useParseMeta();
  const programMeta = useProgramMetaFields();
  const tuitionCost = programMeta?.tuition_cost;

  if (background_color) style["--bg-color"] = `var(${background_color})`;
  if (tuition_color) style["--tuition-color"] = `var(${tuition_color})`;
  if (footnote_background_color) style["--footnote-bg-color"] = `var(${footnote_background_color})`;
  if (footnote_text_color) style["--footnote-color"] = `var(${footnote_text_color})`;

  const handlePhoneNumberClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const target = event.target as HTMLAnchorElement;
    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: "Detail Callout",
        position: "footnote",
        text: target?.innerText || "",
      },
    });
  };

  const renderFootnote = useMemo(() => {
    return footnote ? renderText(parseMetadata ? parseMetadata(footnote) : footnote, phoneNumber) : "";
  }, [footnote, parseMetadata, phoneNumber]);

  return (
    <div className={cn(className, styles.detailCallout)} style={style}>
      <ContentLayout
        className={styles.heading}
        content={main_content}
        heading={{
          variant: "heading-xl",
        }}
        phoneNumber={phoneNumber}
      />
      <Container flex>
        {items?.map((item, index) => {
          if (!item) return null;
          return (
            <Tile
              key={index}
              className={styles.tile}
              content={item}
              padded
              heading={{ variant: "heading-lg" }}
              body={{ variant: "body-sm" }}
              mobileOrientation="vertical"
              desktopOrientation="vertical"
              phoneNumber={phoneNumber}
            />
          );
        })}
      </Container>
      {tuitionCost && display_tuition_cost && (
        <Container flex justifyContent="center" className={styles.tuition}>
          <Heading variant="heading-md" type="h4" className={styles.tuitionLabel}>
            Total cost
          </Heading>
          <div>
            <Heading variant="heading-2xl" type="h4">
              US{formatCurrency(tuitionCost)}
              <sup className={styles.tuitionAsterisk}>*</sup>
            </Heading>
            <Body variant="caption-md" className={styles.tuitionLegal}>
              *Tuition subject to change
            </Body>
          </div>
        </Container>
      )}
      {buttons && buttons?.length > 0 && (
        <ButtonGroup placement="center">
          {buttons.map((button, index) => (
            <Button
              location={ctaLocation}
              key={`${button?.sys.id}-${index}`}
              {...button?.fields}
              phoneNumber={phoneNumber}
            >
              {button?.fields.label}
            </Button>
          ))}
        </ButtonGroup>
      )}
      {footnote ? (
        <Body variant="body-sm" className={styles.footnote}>
          {renderFootnote}
          <a onClick={handlePhoneNumberClick} href={`tel:${phoneNumber}`}>
            {phoneNumber}
          </a>
        </Body>
      ) : (
        <>
          {/* empty div to hold space for gap */}
          <div />
        </>
      )}
    </div>
  );
};
